'use client'

import Link from 'next/link'
import { useAuth } from '../contexts/AuthContext'

export default function AboutPage() {
  const { user } = useAuth()
  
  return (
    <div className="min-h-screen bg-cream text-ink">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-ink/[0.06] bg-cream/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="font-serif italic font-semibold text-2xl text-ink tracking-tight">
            Deeliver
          </Link>
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link href="/about" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              About
            </Link>
            <Link href="/how-it-works" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              How it works
            </Link>
            <Link href="/faq" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              FAQ
            </Link>
            <Link href="/register?type=driver" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              Become a driver
            </Link>
            {user ? (
              <Link 
                href={
                  user.type === 'student' ? '/student/orders' :
                  user.type === 'parent' ? '/rider/new-order' :
                  user.type === 'driver' ? '/driver/schedule' :
                  user.type === 'admin' ? '/admin' : '/login'
                }
                className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]"
              >
                Dashboard
              </Link>
            ) : null}
            <Link 
              href="/rider/new-order" 
              className="bg-ember text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-semibold hover:bg-ember-hover transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-ember focus:ring-offset-2 focus:ring-offset-cream min-h-[44px] flex items-center justify-center"
            >
              Start an order
            </Link>
          </div>
          <div className="md:hidden">
            <Link 
              href="/rider/new-order" 
              className="bg-ember text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-ember-hover transition-colors"
            >
              Order
            </Link>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-8 py-16">
        <div className="mb-8">
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-ink mb-3">About Deeliver</h1>
          <p className="text-ink-soft">Learn more about our mission and community</p>
        </div>
        
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-cream-deep space-y-6 text-gray-700 leading-relaxed">
          <section>
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink mb-4">Our Mission</h2>
            <p>
              Deeliver connects parents with reliable drivers to deliver home-cooked meals to students at UT Austin. 
              We believe that students deserve access to nutritious, home-cooked food while away at college, and we make 
              it easy for parents to send meals directly to campus.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink mb-4">How We Started</h2>
            <p>
              Founded with the goal of bridging the gap between home and campus, Deeliver was created to solve a simple 
              problem: parents want to send their children home-cooked meals, but traditional delivery services are expensive 
              and unreliable. We've built a community-driven platform that makes meal delivery affordable, predictable, and 
              personal.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink mb-4">What Makes Us Different</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Lower Costs:</strong> By batching deliveries and using community drivers, we keep prices affordable for families.</li>
              <li><strong>Predictable Schedules:</strong> Regular routes mean you know exactly when meals will arrive.</li>
              <li><strong>Verified Drivers:</strong> All drivers must submit a valid driver&apos;s license and proof of insurance, which we verify before they can accept any orders.</li>
              <li><strong>Direct Communication:</strong> Connect directly with drivers and get updates throughout the delivery process.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink mb-4">Our Community</h2>
            <p>
              Deeliver is built by and for the UT Austin community. Our drivers are often parents, students, or community 
              members who understand the importance of home-cooked meals. We're committed to creating a reliable, trustworthy 
              service that brings families closer together, even when they're miles apart.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink mb-4">Contact Us</h2>
            <p>
              Have questions or feedback? We'd love to hear from you. Reach out through our support channels or email us 
              directly. We're always working to improve the Deeliver experience.
            </p>
          </section>
        </div>

        <div className="mt-12">
          <Link 
            href="/"
            className="inline-block bg-ember text-white px-6 py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors min-h-[44px]"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  )
}
